import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Download } from "lucide-react";
import React, { useState } from "react";

import { importSalary } from "@/api/cashflow";
import { Button } from "@/components/ui/button";
import { formatRub } from "@/lib/format";
import { ErrorPanel } from "@/pages/cashflow/ManualFields";
import { errorDetail } from "@/pages/cashflow/utils";
import type { CashflowSalaryImportResult } from "@/types/api";

interface Props {
  /** Дата расчёта календаря — от её месяца считается `start_date` серий. */
  asOf: string;
  /** Вызывается после успешного импорта — страница пересчитывает календарь. */
  onImported: () => void;
}

function seriesLine(title: string, amount: string, day: number | null | undefined): string {
  return `${title}: ${formatRub(amount)}, ${day ?? "?"}-го`;
}

/**
 * Кнопка «Импортировать зарплату из Google» и постоянная панель результата.
 *
 * Панель не тост: что импортировано, что пропущено и что погашено, должно
 * оставаться на экране — по этим строкам бухгалтер и правит Свод.
 */
export function SalaryImportCard({ asOf, onImported }: Props): React.ReactElement {
  const qc = useQueryClient();
  const [result, setResult] = useState<CashflowSalaryImportResult | null>(null);

  const mutation = useMutation({
    mutationFn: () => importSalary(asOf || undefined),
    // Панель прошлого импорта под баннером ошибки читалась бы как результат
    // текущего запуска, поэтому гасим её и на старте, и на ошибке.
    onMutate: () => setResult(null),
    onError: () => setResult(null),
    onSuccess: (data) => {
      setResult(data);
      qc.invalidateQueries({ queryKey: ["cashflow-series"] });
      qc.invalidateQueries({ queryKey: ["cashflow-calendar"] });
      onImported();
    },
  });

  const error = mutation.isError ? errorDetail(mutation.error) : null;

  return (
    <section className="space-y-3 rounded-md border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          <Download className="mr-2 h-4 w-4" />
          Импортировать зарплату из Google
        </Button>
        <p className="text-xs text-muted-foreground">
          Лист «Свод», блок «По юрлицам»: аванс 25-го, зарплата 10-го, НДФЛ и взносы 28-го.
          Повторный импорт обновляет те же серии, ручные не трогает.
        </p>
      </div>

      {mutation.isPending && <p className="text-sm text-muted-foreground">Читаем «Свод»…</p>}

      {error !== null && <ErrorPanel detail={`Импорт зарплаты не удался: ${error}`} />}

      {result !== null && (
        <div className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">
            Импорт от {new Date(result.imported_at).toLocaleString("ru-RU")}, таблица{" "}
            <span className="font-mono">{result.sheet_id}</span>
          </p>

          {result.imported.length === 0 ? (
            <p className="text-muted-foreground">
              Ни одна строка Свода не сопоставилась с организациями плана
            </p>
          ) : (
            <ul className="space-y-2">
              {result.imported.map((org) => (
                <li key={org.inn} className="space-y-1">
                  <p className="font-medium">
                    {org.org_name}{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      (строка Свода «{org.label}», ИНН {org.inn})
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    ФОТ на руки {formatRub(org.fot)} · НДФЛ {formatRub(org.ndfl)} · взносы{" "}
                    {formatRub(org.vznosy)}
                  </p>
                  <ul className="ml-4 list-disc text-xs">
                    {org.series.map((s) => (
                      <li key={s.id}>{seriesLine(s.title, s.amount, s.day_of_month)}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}

          {result.skipped.length > 0 && (
            <div className="space-y-1">
              <h4 className="text-xs font-semibold uppercase text-muted-foreground">
                Пропущены строки Свода
              </h4>
              <ul className="ml-4 list-disc text-xs">
                {result.skipped.map((row, index) => (
                  <li key={`${index}-${row.label}`}>
                    {row.label} — {row.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.deactivated.length > 0 && (
            <div className="space-y-1">
              <h4 className="text-xs font-semibold uppercase text-muted-foreground">
                Выключены серии (организации нет в Своде)
              </h4>
              <ul className="ml-4 list-disc text-xs">
                {result.deactivated.map((key) => (
                  <li key={key} className="font-mono">
                    {key}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
